import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppContextConstants } from '@constants/appContextConstants';
import { PublicRoutes } from '@enums/publicRouteEnums';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import { getInitialState } from '@services/authStateService';
import { UserInformationModel } from '@models/login/userInformationModel';
import { CompanyResponseModel } from '@models/login/companyResponseModel';

interface UseCrossTabAppSyncParams {
  isAuthorized: boolean;
  user: UserInformationModel | null;
  setUser: (user: UserInformationModel | null) => void;
  setIsAuthorized: (isAuthorized: boolean) => void;
  setCompanyInfo: (companyInfo: CompanyResponseModel | null) => void;
  setCompanyId: (companyId: string) => void;
  resetLogin: () => void;
  clearHistory: () => void;
}

export function useCrossTabAppSync({
  isAuthorized,
  user,
  setUser,
  setIsAuthorized,
  setCompanyInfo,
  setCompanyId,
  resetLogin,
  clearHistory,
}: UseCrossTabAppSyncParams) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key !== AppContextConstants.AUTH_STORAGE_KEY) {
        return;
      }

      if (e.newValue === null) {
        if (isAuthorized) {
          setUser(null);
          setIsAuthorized(false);
          setCompanyInfo(null);
          setCompanyId(AppContextConstants.EMPTY_STRING);
          resetLogin();
          clearHistory();
          queryClient.clear();
          if (location.pathname !== PublicRoutes.LOGIN) {
            navigate(PublicRoutes.LOGIN);
          }
        }
        return;
      }

      if (e.newValue) {
        try {
          const state = await getInitialState();
          setUser(state.user);
          setCompanyInfo(state.companyInfo);
          setIsAuthorized(state.isAuthorized);
          setCompanyId(state.companyId);
          if (state.isAuthorized && location.pathname === PublicRoutes.LOGIN) {
            navigate(PrivateRoutes.ROOT);
          }
        } catch (error) {
          console.error('Failed to sync auth state from storage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [
    isAuthorized,
    user,
    location.pathname,
    navigate,
    resetLogin,
    clearHistory,
    queryClient,
    setUser,
    setIsAuthorized,
    setCompanyInfo,
    setCompanyId,
  ]);
}
